import type { GoogleAnalyticsEvent, TrackingEntry } from './types.js'

/**
 * A class to send tracking event
 * @class TrackElements
 */
export default class TrackElements {
	entries: TrackingEntry[]
	observer: IntersectionObserver

	constructor(entries: TrackingEntry[]) {
		this.entries = entries

		this.handleElementIntersecting = this.handleElementIntersecting.bind(this)
		this.observer = new IntersectionObserver(this.handleElementIntersecting)

		this.addEvents = this.addEvents.bind(this)
		this.handleElementClick = this.handleElementClick.bind(this)
		this.observeElement = this.observeElement.bind(this)
		this.handleElementIntersecting = this.handleElementIntersecting.bind(this)
	}

	init() {
		if (!Array.isArray(this.entries)) {
			console.error('[TrackElements] Entries should be an array')
			return
		}
		this.entries.forEach(this.addEvents)
	}

	/**
	 * Add event regarding click and / or display key
	 * @param entry TrackingEntry
	 */
	addEvents(entry: TrackingEntry) {
		if (typeof entry.click === 'object') {
			entry.element?.addEventListener('click', this.handleElementClick.bind(this, entry))
		}

		if (typeof entry.display === 'object') {
			let observer = this.observer

			if (entry.intersectionObserverOptions) {
				observer = new IntersectionObserver(
					this.handleElementIntersecting,
					entry.intersectionObserverOptions
				)
			}

			this.observeElement(entry, observer)
		}
	}

	/**
	 * Send tracking on click
	 * @param entry TrackingEntry
	 * @param event Event
	 */
	handleElementClick(entry: TrackingEntry, event: Event) {
		const element = entry.element as HTMLElement
		const href = element.getAttribute('href') ?? ''
		const hrefIsAnchor = href.startsWith('#')
		const openNewTab = element.getAttribute('target') === '_blank'
		const shouldDelayNavigation = href && !openNewTab && !hrefIsAnchor

		if (typeof entry.click === 'object') {
			if (shouldDelayNavigation) {
				event.preventDefault()
				entry.click.eventCallback = () => window.location.assign(href)
			}

			TrackElements.sendEvent(entry.click)
		}
	}

	/**
	 * Observe element
	 * @param entry TrackingEntry
	 * @param observer Default observer of custom with options
	 */
	observeElement(entry: TrackingEntry, observer: IntersectionObserver) {
		if (entry.element) {
			// @ts-expect-error
			entry.element._trackingEvent = entry.display
			observer.observe(entry.element)
		}
	}

	/**
	 * Send event when element is intersecting
	 * @param entries Array<IntersectionObserverEntry>
	 */
	handleElementIntersecting(
		entries: Array<IntersectionObserverEntry>,
		observer: IntersectionObserver
	) {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				// @ts-expect-error
				TrackElements.sendEvent(entry.target._trackingEvent)
				observer.unobserve(entry.target)
			}
		})
	}

	/**
	 * Send tracking event
	 * @static
	 * @param event GoogleAnalyticsEvent
	 */
	static sendEvent(event: GoogleAnalyticsEvent) {
		window.dataLayer.push(event)
	}
}
